
import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

interface FindPlacesButtonProps {
  isMobile: boolean;
}

export default function FindPlacesButton({ isMobile }: FindPlacesButtonProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  useEffect(() => {
    // Focus the input when search is opened
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);
  
  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchOpen && inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [searchOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm) {
      // Set the search term in session storage so the search page can use it
      sessionStorage.setItem('headerSearchTerm', searchTerm);
      // Navigate to find-places with search parameter
      navigate(`/find-places?search=${encodeURIComponent(searchTerm)}`);
      setSearchOpen(false);
      setSearchTerm("");
    }
  };

  if (!isMobile) return null;
  
  return (
    <div className="relative">
      {searchOpen ? (
        <form onSubmit={handleSearch} className="animate-in slide-in-from-top duration-300 absolute top-full right-0 mt-1">
          <div className="relative flex items-center">
            <Input
              ref={inputRef}
              type="text"
              placeholder={t("search.placeholder") || "Search places..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-[180px] pr-8"
            />
            <Button 
              type="button" 
              variant="ghost" 
              size="icon" 
              className="absolute right-0"
              onClick={() => setSearchOpen(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close search</span>
            </Button>
          </div>
        </form>
      ) : (
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => setSearchOpen(true)}
          className="animate-in fade-in duration-300"
        >
          <Search className="h-[1.5rem] w-[1.5rem]" /> {/* Increased size from 1.2rem to 1.5rem */}
          <span className="sr-only">Find Places</span>
        </Button>
      )}
    </div>
  );
}
